<template>
  <div class="empty-state-container">
    <div class="empty-state-card">
      <!-- Animated background elements -->
      <div class="bg-decoration">
        <div class="floating-circle circle-1"></div>
        <div class="floating-circle circle-2"></div>
        <div class="floating-circle circle-3"></div>
      </div>
      
      <!-- Main content -->
      <div class="content-wrapper">
        <div class="icon-container">
          <div class="icon-background">
            <svg class="empty-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 12h8M8 8h8M8 16h5M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" 
                    stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
        </div>
        
        <div class="text-content">
          <h3 class="title">No Messages Yet</h3>
          <p class="subtitle">{{ isGroupChat ? 'Start the conversation in this group' : 'Send a message to start chatting' }}</p>
        </div>
        
        <button class="cta-button" @click="startConversation" v-if="!isGroupChat">
          <span>Start Conversation</span>
          <svg class="button-icon" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clip-rule="evenodd" />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'NoMessages',
  props: {
    isGroupChat: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    startConversation() {
      this.$emit('start-conversation');
    }
  }
};
</script>

<style scoped>
.empty-state-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 2rem;
  position: relative;
  overflow: hidden;
  min-height: 300px;
}

.empty-state-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 420px;
  height: auto;
  min-height: 320px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.08),
    0 0 0 1px rgba(255, 255, 255, 0.05);
  overflow: hidden;
  animation: slideUp 0.6s ease-out;
  padding: 2rem 1.5rem;
}

.bg-decoration {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 1;
}

.floating-circle {
  position: absolute;
  border-radius: 50%;
  background: linear-gradient(45deg, rgba(102, 126, 234, 0.08), rgba(118, 75, 162, 0.08));
  animation: float 6s ease-in-out infinite;
}

.circle-1 {
  width: 60px;
  height: 60px;
  top: 5%;
  right: 10%;
  animation-delay: 0s;
}

.circle-2 {
  width: 45px;
  height: 45px;
  bottom: 15%;
  left: 8%;
  animation-delay: 2s;
}

.circle-3 {
  width: 30px;
  height: 30px;
  top: 60%;
  right: 20%;
  animation-delay: 4s;
}

.content-wrapper {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 100%;
}

.icon-container {
  margin-bottom: 1.5rem;
  animation: bounce 2s ease-in-out infinite;
}

.icon-background {
  width: 90px;
  height: 90px;
  background: linear-gradient(135deg, #3b7ca1, #084468);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 
    0 10px 30px rgba(8, 68, 104, 0.2),
    0 0 0 8px rgba(8, 68, 104, 0.08);
  position: relative;
  overflow: hidden;
}

.icon-background::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.15), transparent);
  animation: shimmer 3s ease-in-out infinite;
}

.empty-icon {
  width: 40px;
  height: 40px;
  color: white;
  z-index: 1;
}

.text-content {
  margin-bottom: 1.5rem;
}

.title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #2d3748;
  margin: 0 0 0.5rem 0;
}

.subtitle {
  font-size: 0.95rem;
  color: #64748b;
  margin: 0;
  line-height: 1.5;
}

.cta-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.75rem;
  background: linear-gradient(135deg, #3b7ca1, #084468);
  color: white;
  border: none;
  border-radius: 50px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(8, 68, 104, 0.25);
  position: relative;
  overflow: hidden;
}

.cta-button::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s ease;
}

.cta-button:hover::before {
  left: 100%;
}

.cta-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(8, 68, 104, 0.35);
}

.cta-button:active {
  transform: translateY(0);
}

.button-icon {
  width: 18px;
  height: 18px;
  transition: transform 0.3s ease;
}

.cta-button:hover .button-icon {
  transform: translateX(4px);
}

/* Animations */
@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
  }
  33% {
    transform: translateY(-8px) rotate(120deg);
  }
  66% {
    transform: translateY(5px) rotate(240deg);
  }
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-8px);
  }
  60% {
    transform: translateY(-4px);
  }
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%) translateY(-100%) rotate(45deg);
  }
  100% {
    transform: translateX(100%) translateY(100%) rotate(45deg);
  }
}

/* Dark mode support */
.theme--dark .empty-state-card {
  background: rgba(26, 42, 58, 0.95);
  border-color: rgba(255, 255, 255, 0.05);
}

.theme--dark .title {
  color: #e9edef;
}

.theme--dark .subtitle {
  color: #8696a0;
}

.theme--dark .floating-circle {
  background: linear-gradient(45deg, rgba(102, 126, 234, 0.15), rgba(118, 75, 162, 0.15));
}

/* Responsive design */
@media (max-width: 768px) {
  .empty-state-container {
    padding: 1.5rem;
    min-height: 250px;
  }
  
  .empty-state-card {
    min-height: 280px;
    border-radius: 20px;
    padding: 1.5rem 1.25rem;
  }
  
  .icon-background {
    width: 75px;
    height: 75px;
  }
  
  .icon-background::before {
    width: 150%;
    height: 150%;
  }
  
  .empty-icon {
    width: 34px;
    height: 34px;
  }
  
  .title {
    font-size: 1.25rem;
  }
  
  .subtitle {
    font-size: 0.85rem;
  }
  
  .cta-button {
    padding: 0.65rem 1.5rem;
    font-size: 0.85rem;
  }
  
  .circle-1 {
    width: 45px;
    height: 45px;
  }
  
  .circle-2 {
    width: 35px;
    height: 35px;
  }
  
  .circle-3 {
    width: 25px;
    height: 25px;
  }
}

@media (max-width: 480px) {
  .empty-state-container {
    padding: 1rem;
    min-height: 200px;
  }
  
  .empty-state-card {
    min-height: 240px;
    border-radius: 16px;
    padding: 1.25rem 1rem;
  }
  
  .icon-background {
    width: 65px;
    height: 65px;
  }
  
  .empty-icon {
    width: 30px;
    height: 30px;
  }
  
  .title {
    font-size: 1.1rem;
  }
  
  .subtitle {
    font-size: 0.8rem;
  }
  
  .cta-button {
    padding: 0.5rem 1.25rem;
    font-size: 0.8rem;
  }
  
  .icon-container {
    margin-bottom: 1rem;
  }
  
  .text-content {
    margin-bottom: 1.25rem;
  }
}
</style>